// CR051: Capture Setup Tab - Merchant Binding Configuration
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Save, RotateCcw, Eye, Play, AlertCircle, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { MerchantBinding, RecipeTemplate } from '@/pages/admin/FlyerCaptureConsole';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CaptureSetupTabProps {
  merchant: MerchantBinding;
  recipes: RecipeTemplate[];
  onUpdate: (merchant: MerchantBinding) => void;
}

const DEFAULT_SELECTORS_OVERRIDE = {
  cookie_accept_selector: "",
  cookie_reject_selector: "",
  postal_input_selector: "",
  postal_submit_selector: "",
  store_pick_selector: "",
  confirm_location_selector: "",
  flyer_open_selector: "",
  next_button_selector: "",
  prev_button_selector: "",
  page_indicator_selector: "",
  viewer_container_selector: "",
  close_modal_selector: ""
};

export const CaptureSetupTab = ({ merchant, recipes, onUpdate }: CaptureSetupTabProps) => {
  const [formState, setFormState] = useState<MerchantBinding>(merchant);
  const [overridesJson, setOverridesJson] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [lastRunResult, setLastRunResult] = useState<{ success: boolean; pagesCaptured: number; errors: string[] } | null>(null);

  useEffect(() => {
    setFormState(merchant);
    setOverridesJson(JSON.stringify(merchant.merchant_overrides, null, 2));
    setHasChanges(false);
    setLastRunResult(null);
  }, [merchant]);

  const handleChange = <K extends keyof MerchantBinding>(key: K, value: MerchantBinding[K]) => {
    setFormState(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleOverridesChange = (json: string) => {
    setOverridesJson(json);
    setHasChanges(true);
    try {
      const parsed = JSON.parse(json);
      setFormState(prev => ({ ...prev, merchant_overrides: parsed }));
    } catch {
      // Invalid JSON, keep the text but don't update state
    }
  };

  const handleSave = () => {
    try {
      // Validate JSON
      const overrides = JSON.parse(overridesJson);
      onUpdate({ ...formState, merchant_overrides: overrides });
      setHasChanges(false);
    } catch {
      toast.error('Invalid JSON in merchant overrides');
    }
  };

  const handleReset = () => {
    setFormState(merchant);
    setOverridesJson(JSON.stringify(merchant.merchant_overrides, null, 2));
    setHasChanges(false);
  };

  const selectedRecipe = recipes.find(r => r.id === formState.recipe_id);

  // Compute effective config (merged recipe defaults + merchant overrides)
  const effectiveConfig = {
    ...(selectedRecipe?.recipe_defaults || {}),
    ...formState.merchant_overrides,
    start_url: formState.start_url,
    postal_code: formState.postal_code
  };

  // Handle run capture with proper authorization
  const handleRunCapture = async () => {
    setIsRunning(true);
    setLastRunResult(null);
    
    try {
      // Get session for authorization header
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        toast.error('Please sign in to run captures');
        setLastRunResult({ 
          success: false, 
          pagesCaptured: 0, 
          errors: ['Authentication required. Please sign in.'] 
        });
        setIsRunning(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('capture-console-run', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { bindingId: merchant.id, recipeId: formState.recipe_id }
      });
      
      if (error) throw error;
      
      setLastRunResult({ 
        success: data.success, 
        pagesCaptured: data.pagesCaptured || 0, 
        errors: data.errors || [] 
      });
      
      if (data.success) {
        toast.success(`Capture completed: ${data.pagesCaptured} pages`);
      } else {
        toast.error(`Capture failed: ${data.errors?.[0] || 'Unknown error'}`);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('[CaptureSetupTab] Run capture error:', err);
      setLastRunResult({ 
        success: false, 
        pagesCaptured: 0, 
        errors: [errorMessage] 
      });
      toast.error(`Capture failed: ${errorMessage}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Core Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Capture Configuration</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={!hasChanges}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!hasChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Configure how flyers are captured for {merchant.merchant_name}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_url">Start URL</Label>
              <Input
                id="start_url"
                value={formState.start_url}
                onChange={(e) => handleChange('start_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input
                id="postal_code"
                value={formState.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="M6K 3R8"
              />
            </div>
          </div>

          {/* Recipe Selection */}
          <div className="space-y-2">
            <Label>Recipe Template</Label>
            <Select
              value={formState.recipe_id || 'none'}
              onValueChange={(v) => handleChange('recipe_id', v === 'none' ? null : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a recipe..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Recipe</SelectItem>
                {recipes.map(recipe => (
                  <SelectItem key={recipe.id} value={recipe.id}>
                    {recipe.recipe_name}
                    {recipe.is_system_template && <Badge variant="secondary" className="ml-2">System</Badge>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Run Strategy */}
          <div className="space-y-2">
            <Label>Run Strategy</Label>
          <Select
            value={formState.run_strategy}
            onValueChange={(v) => handleChange('run_strategy', v as 'STRICT_RECIPE' | 'RECIPE_WITH_FALLBACK')}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
              <SelectContent>
                <SelectItem value="STRICT_RECIPE">Strict Recipe (fail if recipe fails)</SelectItem>
                <SelectItem value="RECIPE_WITH_FALLBACK">Recipe with Fallback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Automatic Capture</Label>
              <p className="text-sm text-muted-foreground">Allow scheduled runs for this merchant</p>
            </div>
            <Switch
              checked={formState.enabled}
              onCheckedChange={(v) => handleChange('enabled', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Merchant Overrides */}
      <Card>
        <CardHeader>
          <CardTitle>Merchant Overrides</CardTitle>
          <CardDescription>
            JSON configuration for selectors and behavior overrides
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible>
            <AccordionItem value="overrides">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <span>Override Configuration</span>
                  {Object.keys(formState.merchant_overrides || {}).length > 0 && (
                    <Badge variant="secondary">
                      {Object.keys(formState.merchant_overrides || {}).length} overrides
                    </Badge>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Textarea
                  value={overridesJson}
                  onChange={(e) => handleOverridesChange(e.target.value)}
                  className="font-mono text-sm min-h-[200px]"
                  placeholder={JSON.stringify(DEFAULT_SELECTORS_OVERRIDE, null, 2)}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Effective Config Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Effective Configuration
          </CardTitle>
          <CardDescription>
            Merged recipe defaults + merchant overrides (read-only preview)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-muted p-4 rounded-lg text-sm overflow-auto max-h-[300px]">
            {JSON.stringify(effectiveConfig, null, 2)}
          </pre>
        </CardContent>
      </Card>

      {/* Run Capture Now */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Test Capture
          </CardTitle>
          <CardDescription>
            Run a capture now using the current configuration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={handleRunCapture}
            disabled={isRunning || hasChanges}
            className="w-full"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running Capture...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Capture Now
              </>
            )}
          </Button>
          
          {hasChanges && (
            <div className="flex items-center gap-2 text-sm text-amber-600">
              <AlertCircle className="h-4 w-4" />
              Save changes before running
            </div>
          )}

          {lastRunResult && (
            <div className={`p-4 rounded-lg border ${lastRunResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {lastRunResult.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className={`font-medium ${lastRunResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {lastRunResult.success ? 'Capture Completed' : 'Capture Failed'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Pages captured: {lastRunResult.pagesCaptured}
              </p>
              {lastRunResult.errors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {lastRunResult.errors.map((err, i) => (
                    <p key={i} className="text-sm text-red-600">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
